import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
    API_URL,
    AsEntityTypeNames,
    entityTypeNameById,
    Module,
    RecentlyItem,
    RequirementDetailsTabsEnum
} from '@frontend-site/shared/data';
import { ProjectStore } from './project';
import { Params } from '@angular/router';
import { AngularModeService } from './angular-mode';

@Injectable({ providedIn: 'root' })
export class RecentlyViewedService {
    constructor(
        @Inject(API_URL) private readonly apiUrl: string,
        private http: HttpClient,
        private readonly projectStore: ProjectStore,
        private readonly angularModeService: AngularModeService
    ) {}

    addItem(entityId: number, projectId: number): void {
        this.http.post(`${this.apiUrl}/recentlyViewed`, entityId, { params: { projectId } }).subscribe();
    }

    getItems(projectId: number): Observable<RecentlyItem[]> {
        const params = new HttpParams().set('projectId', projectId);
        return this.http.get<RecentlyItem[]>(`${this.apiUrl}/recentlyViewed`, { params }).pipe(
            map((res: RecentlyItem[]) => {
                return res.map((item) => {
                    const type: string | undefined = entityTypeNameById.get(item.typeId);
                    item.url = this.getUrl(type, projectId, item.entityDetailsPageId);
                    item.queryParams = this.getQueryParams(item.typeName);
                    item.typeName;
                    item.title;
                    return item;
                });
            })
        );
    }

    getUrl(type: string | undefined, projectId: number, urlParam: number | null): string {
        switch (type) {
            case AsEntityTypeNames.DEFECT:
                return `/${projectId}/defects/details/${urlParam}`;
            case AsEntityTypeNames.CORRECTION:
                return `/${projectId}/corrections/details/${urlParam}`;
            case AsEntityTypeNames.FEATURE:
                return `/${projectId}/development/feature/${urlParam}`;
            case AsEntityTypeNames.TASK:
                return `/${projectId}/development/task/${urlParam}`;
            case AsEntityTypeNames.REQUIREMENT:
                if (this.isRdxEnabled) {
                    return `/${projectId}/development/requirement/${urlParam}`;
                } else {
                    return `/${projectId}/requirements/requirement/${urlParam}`;
                }
            case AsEntityTypeNames.TEST:
                if (this.angularModeService.isNewUi(Module.TestTree)) {
                    return `/${projectId}/tests/directTestDetails/${urlParam}`;
                }
                return `/tm/${projectId}/execution/directTestDetails/${urlParam}`;
            case AsEntityTypeNames.STEP:
                if (this.angularModeService.isNewUi(Module.TestTree)) {
                    return `/${projectId}/tests/directTestDetails/${urlParam}?tabName=STEPS`;
                }
                return `/tm/${projectId}/execution/directTestDetails/${urlParam}?tabName=STEPS`;
            case AsEntityTypeNames.SCRIPT:
                if (this.angularModeService.isNewUi(Module.TestTree)) {
                    return `/${projectId}/tests/directTestDetails/${urlParam}?tabName=SCRIPT`;
                }
                return `/tm/${projectId}/execution/directTestDetails/${urlParam}`;
            case AsEntityTypeNames.PA_TEST_SET:
                return `/${projectId}/testSets/details/${urlParam}`;
            case AsEntityTypeNames.PA_EXECUTION_SCHEDULING:
                return `/${projectId}/paExecutionScheduling/details/${urlParam}`;
        }
        return 'UNKNOWN';
    }

    private getQueryParams(typeName: string | undefined): Params | undefined {
        let queryParams: Params | undefined;
        if (typeName === AsEntityTypeNames.ANALYSIS) {
            queryParams = { tabName: RequirementDetailsTabsEnum.RISK_ANALYSIS };
        }
        return queryParams;
    }

    get isRdxEnabled(): boolean | undefined {
        return this.projectStore.projectDetails?.rdxEnabled;
    }
}
